export type ParticipantRole = "reader" | "editor" | "owner"

export type Participant = {
  oid: string
  display_name: string
  type: "user" | "group"
  role: ParticipantRole
}

export type TimeSavedUnit = "week" | "month" | "once"
export type OpCostUnit = "monthly" | "yearly"
export type ImplTime = "" | "< 1 week" | "< 1 month" | "1-3 months" | "3-6 months"
export type FeasibilityValue = "1" | "2" | "3"
export type StatusIndex = 0 | 1 | 2 | 3
export type ToggleValue = "yes" | "no"

export type UseCaseData = {
  // Cosmos fields
  id: string
  ownerUserId: string
  created: string
  updated: string
  participants: Participant[]
  newParticipantNotifications: string[]
  isUnrestricted?: boolean
  currentUserRole?: string
  hasUnviewedNotification?: boolean
  qaScore?: number
  qaGrade?: string
  qaReviewedAt?: string
  qaReviewData?: {
    summary: string
    issues: Array<{ severity: "high" | "medium" | "low"; field: string; message: string }>
    suggestions: string[]
  }
  salesPitchText?: string
  salesPitchGeneratedAt?: string
  voteCount?: number
  hasVoted?: boolean

  // Core fields (match .txt JSON)
  ucName: string
  owner: string
  timeSavedHours: string
  users: string
  salary: string
  directYear: string
  investment: string
  opCost: string
  timeSavedUnit: TimeSavedUnit
  opCostUnit: OpCostUnit
  implTime: ImplTime
  issue: string
  solutionDesc: string
  dataNeeded: string
  comments: string

  // Multi-select arrays
  bpSel: string[]
  saSel: string[]
  efSel: string[]
  siteAreaSel: string[]

  // Sidebar fields
  feasV: FeasibilityValue
  statIdx: StatusIndex
  togTS: ToggleValue
  togQI: ToggleValue
  togRD: ToggleValue
  togWW: ToggleValue
}

export type UseCaseFormData = Omit<
  UseCaseData,
  "id" | "ownerUserId" | "created" | "updated" | "participants" | "newParticipantNotifications"
>

export type CalculatedKPIs = {
  annualHoursPerPerson: number
  hourlyRate: number
  totalCapacityHours: number
  fteEquivalent: number
  softSavings: number
  hardSavings: number
  annualBenefit: number
  opCostYearly: number
  roi12m: number
  tenkQuarterly: number
  tenkCategory: TenKCategory
}

export type TenKCategory = "Minimal" | "Realistic" | "Ambitious" | "Low success rate"

export type StatusConfig = {
  text: string
  className: string
  color: string
}

export type FeasibilityConfig = {
  label: string
  color: string
  className: string
}

export type SortDirection = "asc" | "desc"

export type UseCaseFilters = {
  statuses: Set<string>
  tenkCategories: Set<string>
  feasibilities: Set<string>
  sites: Set<string>
  searchText: string
}

export type DashboardUseCase = UseCaseData &
  CalculatedKPIs & {
    statusText: string
    statusClassName: string
    feasLabel: string
    feasClassName: string
    sites: string[]
    hasUnviewedNotification?: boolean
  }

// Constants

export const STATUSES: StatusConfig[] = [
  { text: "Implemented", className: "b-green", color: "hsl(var(--success-ink))" },
  { text: "In Progress", className: "b-amber", color: "hsl(var(--warning-ink))" },
  { text: "Planned", className: "b-grey", color: "hsl(var(--muted-foreground))" },
  { text: "On Hold", className: "b-red", color: "hsl(var(--destructive-ink))" },
]

export const FEASIBILITY_MAP: Record<string, FeasibilityConfig> = {
  "1": { label: "Low/Complex", color: "hsl(var(--destructive-ink))", className: "fb-low" },
  "2": { label: "Medium", color: "hsl(var(--warning-ink))", className: "fb-med" },
  "3": { label: "High/Easy", color: "hsl(var(--success-ink))", className: "fb-high" },
}

export const TENK_COLORS: Record<TenKCategory, string> = {
  Realistic: "hsl(var(--success-ink))",
  Ambitious: "hsl(var(--warning-ink))",
  Minimal: "hsl(var(--muted-foreground))",
  "Low success rate": "hsl(var(--destructive-ink))",
}

export const STATUS_COLORS: Record<string, string> = {
  Implemented: "hsl(var(--success-ink))",
  "In Progress": "hsl(var(--warning-ink))",
  Planned: "hsl(var(--muted-foreground))",
  "On Hold": "hsl(var(--destructive-ink))",
}

export const BUSINESS_PAINS = [
  "Cost",
  "Speed",
  "Quality",
  "Scalability",
  "Bottleneck",
  "Compliance",
]

export const STRATEGIC_ALIGNMENTS = [
  { value: "CE", label: "Commercial Excellence (CE)" },
  { value: "OE", label: "Operational Excellence (OE)" },
  { value: "R&D", label: "Research & Development (R&D)" },
]

export const ENTERPRISE_FUNCTIONS = [
  "Human Resources",
  "Production & Mfg",
  "Technology & Projects",
  "Purchasing",
  "Customer Service",
  "Law & Regulatory",
  "Marketing & Sales",
  "Logistics & SC",
  "IT & Security",
  "R&D",
  "Accounting & Finance",
  "Generally Applicable",
]

export const SITE_AREAS = [
  "Avenue, Sligo - IE",
  "Be-Må Molds, Värnamo - SE",
  "Lövepac Converting, Beijing - CN",
  "Lövepac Technology, Shenzen - CN",
  "Nolato Beijing, Beijing - CN",
  "Nolato Cerbo, Trollhättan - SE",
  "Nolato Contour, Baldwin - US",
  "Nolato Gota, Götene - SE",
  "Nolato GW, Dongguan - CN",
  "Nolato Hungary, Mosonmagyaróvár - HU",
  "Nolato India, Bangalore - IN",
  "Nolato Jabar, Andover - US",
  "Nolato Jaycare, Newcastle - UK",
  "Nolato Lövepac, Skånes Fagerhult - SE",
  "Nolato Malaysia, Penang - MY",
  "Nolato Medical, Beijing - CN",
  "Nolato MediTech, Hörby - SE",
  "Nolato MediTech, Lomma - SE",
  "Nolato MediTor, Torekov - SE",
  "Nolato Polymer, Ängelholm - SE",
  "Nolato Polymer, Torekov - SE",
  "Nolato PPT, Maldon - UK",
  "Nolato Querétaro - MX",
  "Nolato Romania, Prahova - RO",
  "Nolato San Antonio - US",
  "Nolato Silikonteknik, Beijing - CN",
  "Nolato Silikonteknik, Hallsberg - SE",
  "Nolato Stargard, Stargard - PL",
  "Nolato Technology, Dongguan - CN",
  "Nolato Technology, Suzhou - CN",
  "Nolato Treff, Degersheim - CH",
  "Nolato Tucson - US",
  "Nolato Vermont - US",
  "Nolato AB",
  "Medical Solutions",
  "Engineered Solutions",
  "Materials",
  "Group-Wide",
  "Group Procurement",
  "Global Sales",
]

export const SITE_CHART_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
]

export const IMPL_TIME_OPTIONS = [
  { value: "", label: "Select timeframe..." },
  { value: "< 1 week", label: "< 1 week" },
  { value: "< 1 month", label: "< 1 month" },
  { value: "1-3 months", label: "1-3 months" },
  { value: "3-6 months", label: "3-6 months" },
]
